// src/ConsumerApp/Screens/Auth/LoginModal.js

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthModal } from '../../context/AuthModalContext';
import { useToast } from '../../context/ToastContext';
import { requestPermission } from '../../../push-notifications';
import { updateUserInServiceWorker } from '../../../utils/serviceWorkerUtils';
import { guestBucketManager } from '../../utils/guestBucketManager';

// Real SVGs from assets/icons
import X from '../../../assets/icons/X.svg';
import Lightning from '../../../assets/icons/Lightning.svg';
import CheckCircleGreen from '../../../assets/icons/CheckCircleGreen.svg';
import Wallet from '../../../assets/icons/Wallet.svg';

const ICON_WITH_TEXT_LOGO = `${process.env.PUBLIC_URL || ''}/iconwithtext.png`;

const BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET || '';
const LOGIN_URL = `${BASE_URL}customer/customer-login`;
const VERIFY_OTP_URL = `${BASE_URL}customer/verify-otp-and-login`;
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.brickbucket.buyer';

export default function LoginModal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoginModalOpen, closeLoginModal } = useAuthModal();
  const { showToast } = useToast();

  const [step, setStep] = useState('MOBILE');
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const inputRefs = useRef([]);

  useEffect(() => {
    let interval = null;
    if (step === 'OTP' && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  useEffect(() => {
    if (isLoginModalOpen) {
      setStep('MOBILE');
      setOtp(['', '', '', '']);
      setErrorMessage('');
      setTimer(60);
    }
  }, [isLoginModalOpen]);

  if (!isLoginModalOpen) return null;

  const handleSendOtp = async (forceLogin = false) => {
    if (mobileNumber.length !== 10) {
      setErrorMessage('Mobile number must be 10 digits.');
      return;
    }

    if (!['6', '7', '8', '9'].includes(mobileNumber[0])) {
      setErrorMessage('Mobile number must start with 6, 7, 8, or 9.');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const res = await fetch(LOGIN_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile_number: mobileNumber, forceLogin }),
      });

      const data = await res.json();

      if (res.status === 409 && !forceLogin) {
        return handleSendOtp(true);
      }

      if (res.ok) {
        setStep('OTP');
        setTimer(60);
        setTimeout(() => inputRefs.current[0]?.focus(), 150);
      } else if (res.status === 404) {
        sessionStorage.setItem('unregisteredMobile', mobileNumber);
        closeLoginModal();
        navigate('/consumer/UserDetails');
      } else {
        setErrorMessage(data?.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('OTP Send error:', err);
      setErrorMessage('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (forceLogin = false) => {
    const otpString = otp.join('');
    if (otpString.length !== 4) {
      setErrorMessage('Please enter the complete 4-digit code.');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const res = await fetch(VERIFY_OTP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile_number: mobileNumber,
          otp: otpString,
          forceLogin: true,
        }),
      });

      const data = await res.json();
      const userObj = data?.user || data?.data || data;
      const isSuccessfulLogin = res.ok || (res.status === 409 && (userObj?._id || userObj?.token));

      if (!isSuccessfulLogin) {
        setErrorMessage(data?.message || 'Invalid verification code.');
        return;
      }

      const customerRecord = {
        ...userObj,
        _id: userObj._id || userObj.customerId || userObj.id,
      };

      const pendingLink = sessionStorage.getItem('pendingDeepLink');
      console.log('Pending Deep Link:', pendingLink, 'Customer ID:', customerRecord._id);
      if (pendingLink) {
        const params = new URLSearchParams(pendingLink.split('?')[1]);
        const targetCustomerId = params.get('customer_id');

        // Check if the user who just logged in owns this RFQ link
        if (targetCustomerId && customerRecord._id !== targetCustomerId ) {
          setErrorMessage("This RFQ link belongs to a different account. Please use the correct registered mobile number.");
          setLoading(false);
          return;
        }
      }

      localStorage.setItem('userData', JSON.stringify(customerRecord));
      sessionStorage.setItem('userData', JSON.stringify(customerRecord));
      sessionStorage.setItem('customerId', customerRecord._id);
      sessionStorage.setItem('token', customerRecord.token);
      sessionStorage.setItem('hubId', customerRecord.hubId || '');
      sessionStorage.setItem('roleId', customerRecord.roleId || '');

      guestBucketManager.clearGuestBucket();
      localStorage.removeItem('guestCart');
      sessionStorage.removeItem('guestCart');

      window.dispatchEvent(new Event('auth-state-change'));

      try {
        if (typeof requestPermission === 'function') requestPermission();
        if (typeof updateUserInServiceWorker === 'function') updateUserInServiceWorker(customerRecord._id);
      } catch (e) {
        console.warn('Service worker setup skipped:', e);
      }

      queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerRecord._id] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', customerRecord._id] });
      queryClient.invalidateQueries({ queryKey: ['profile', customerRecord._id] });

      const displayName = customerRecord.first_name || customerRecord.name || `+91 ${mobileNumber}`;
      if (pendingLink) {
        sessionStorage.removeItem('pendingDeepLink');
        showToast(`Welcome back, ${displayName}! Redirecting to your RFQ...`, 'success');
        closeLoginModal();
        navigate(pendingLink);
        return;
      }

      showToast(`Welcome back, ${displayName}! You are now logged in.`, 'success');
      closeLoginModal();

      if (customerRecord.customer_status === 1) {
        navigate('/consumer/UserDetails');
      } else if (customerRecord.customer_status === 2) {
        navigate('/consumer/Approved');
      }
    } catch (err) {
      console.error('Verify error:', err);
      setErrorMessage('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    if (value.length > 1) {
      const chars = value.slice(0, 4).split('');
      const updated = [...otp];
      chars.forEach((c, i) => {
        if (i < 4) updated[i] = c;
      });
      setOtp(updated);
      const nextIdx = Math.min(chars.length, 3);
      inputRefs.current[nextIdx]?.focus();
      return;
    }

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="bb-modal-overlay" onClick={closeLoginModal}>
      <style>{`
        /* ══════════════════════════════════════════════════════════
           DESKTOP MODAL VIEWPORT (>= 769px)
           ══════════════════════════════════════════════════════════ */
        .bb-modal-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.65);
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          backdrop-filter: blur(3px);
        }

        .bb-modal-card {
          background-color: #FFFFFF;
          border-radius: 24px;
          width: 100%;
          max-width: 820px;
          display: flex;
          position: relative;
          overflow: hidden;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.3);
          box-sizing: border-box;
        }

        .bb-modal-close-btn {
          position: absolute;
          top: 18px;
          right: 18px;
          background: #F1F5F9;
          border: none;
          width: 34px;
          height: 34px;
          border-radius: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: background-color 0.15s ease;
        }

        .bb-modal-mobile-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 24px;
        }

        .bb-modal-skip-btn {
          display: none;
        }

        .bb-left-visual-col {
          flex: 1;
          background-color: #FFF7F7;
          padding: 44px 36px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          border-right: 1px solid #FFE4E6;
          box-sizing: border-box;
        }

        .bb-brand-logo {
          height: 48px;
          max-height: 52px;
          width: auto;
          object-fit: contain;
        }

        .bb-left-heading {
          font-size: 22px;
          font-weight: 800;
          color: #2B2A29;
          line-height: 28px;
          margin: 0 0 10px 0;
        }

        .bb-left-subtext {
          font-size: 13px;
          color: #64748B;
          line-height: 19px;
          margin: 0 0 26px 0;
        }

        .bb-perks-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .bb-perk-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13.5px;
          font-weight: 700;
          color: #2B2A29;
        }

        .bb-perk-icon-wrap {
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .bb-right-form-col {
          flex: 1.2;
          padding: 40px 38px 24px 38px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          box-sizing: border-box;
        }

        .bb-form-main-section {
          display: flex;
          flex-direction: column;
        }

        .bb-form-header {
          margin-bottom: 20px;
        }

        .bb-main-title {
          font-size: 22px;
          font-weight: 800;
          color: #1E293B;
          margin: 0 0 6px 0;
        }

        .bb-sub-title {
          font-size: 13px;
          color: #64748B;
          line-height: 18px;
        }

        .bb-error-banner {
          background-color: #FEF2F2;
          border: 1px solid #FCA5A5;
          color: #DC2626;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .bb-form-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .bb-phone-input-row {
          display: flex;
          align-items: center;
          border: 1.5px solid #CBD5E1;
          border-radius: 12px;
          height: 50px;
          overflow: hidden;
        }

        .bb-country-code-badge {
          padding: 0 14px;
          background-color: #F8FAFC;
          border-right: 1px solid #CBD5E1;
          height: 100%;
          display: flex;
          align-items: center;
          font-size: 14px;
          font-weight: 600;
          color: #2B2A29;
        }

        .bb-mobile-input {
          flex: 1;
          height: 100%;
          border: none;
          outline: none;
          padding: 0 14px;
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.4px;
          color: #2B2A29;
        }

        .bb-action-btn {
          background-color: #E9001E;
          color: #FFFFFF;
          border: none;
          border-radius: 12px;
          height: 48px;
          font-size: 14.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .bb-legal-disclaimer {
          font-size: 11px;
          color: #94A3B8;
          text-align: center;
          line-height: 16px;
          margin-top: 14px;
        }

        .bb-otp-grid {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin: 6px 0;
        }

        .bb-otp-box {
          width: 56px;
          height: 60px;
          text-align: center;
          font-size: 22px;
          font-weight: 800;
          color: #2B2A29;
          border: 1.5px solid #CBD5E1;
          border-radius: 12px;
          outline: none;
          background-color: #FAFAFA;
        }

        .bb-otp-footer-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
        }

        .bb-change-number-btn {
          background: none;
          border: none;
          color: #0F4392;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
        }

        .bb-resend-wrapper {
          font-size: 12.5px;
        }

        .bb-timer-text {
          color: #64748B;
        }

        .bb-resend-btn {
          background: none;
          border: none;
          color: #E9001E;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          padding: 0;
        }

        .bb-app-download-section {
          margin-top: 26px;
          padding-top: 18px;
          border-top: 1px solid #F1F5F9;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .bb-app-download-heading {
          font-size: 11.5px;
          font-weight: 600;
          color: #64748B;
          text-align: center;
        }

        .bb-badges-row {
          display: flex;
          gap: 12px;
          width: 100%;
          justify-content: center;
        }

        .bb-black-badge {
          flex: 1;
          max-width: 156px;
          height: 44px;
          background-color: #000000;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 0 12px;
          gap: 10px;
          text-decoration: none;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        }

        .bb-black-badge-muted {
          flex: 1;
          max-width: 156px;
          height: 44px;
          background-color: #1E293B;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 0 12px;
          gap: 10px;
          cursor: not-allowed;
          position: relative;
          opacity: 0.9;
        }

        .bb-play-store-svg {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }

        .bb-apple-svg {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }

        .bb-badge-text-wrap {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          line-height: 1;
        }

        .bb-badge-top-text {
          font-size: 8px;
          color: #D1D5DB;
          letter-spacing: 0.4px;
          margin-bottom: 2px;
          text-transform: uppercase;
        }

        .bb-badge-main-text {
          font-size: 12px;
          font-weight: 700;
          color: #FFFFFF;
          letter-spacing: 0.2px;
        }

        .bb-coming-soon-tag {
          position: absolute;
          top: -6px;
          right: -4px;
          background-color: #E9001E;
          color: #FFFFFF;
          font-size: 8px;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 4px;
          letter-spacing: 0.4px;
          white-space: nowrap;
        }

        /* ══════════════════════════════════════════════════════════
           MOBILE VIEWPORT (<= 768px)
           ══════════════════════════════════════════════════════════ */
        @media (max-width: 768px) {
          .bb-modal-overlay {
            padding: 0;
            background-color: #FFFFFF;
            align-items: flex-start;
          }

          .bb-modal-card {
            max-width: 100vw;
            height: 100dvh;
            border-radius: 0;
            border: none;
            box-shadow: none;
            flex-direction: column;
            overflow-y: auto;
          }

          .bb-modal-close-btn {
            display: none;
          }

          /* Corner Header Row: Logo on left, Skip on right */
          .bb-modal-mobile-header {
            margin-bottom: 14px;
          }

          .bb-modal-skip-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background-color: #FFFFFF;
            border: 1px solid #CBD5E1;
            border-radius: 999px;
            padding: 5px 14px;
            font-size: 12.5px;
            font-weight: 700;
            color: #475569;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            cursor: pointer;
            outline: none;
          }

          .bb-left-visual-col {
            padding: 24px 20px 20px 20px;
            border-right: none;
            border-bottom: 1px solid #FFE4E6;
            flex: none;
          }

          .bb-brand-logo {
            height: 38px;
            max-height: 40px;
            margin-bottom: 0;
          }

          .bb-left-heading {
            font-size: 18px;
            line-height: 24px;
            margin-bottom: 6px;
          }

          .bb-left-subtext {
            font-size: 12px;
            line-height: 17px;
            margin-bottom: 14px;
          }

          .bb-perks-list {
            gap: 10px;
          }

          .bb-perk-item {
            font-size: 12.5px;
            gap: 10px;
          }

          .bb-right-form-col {
            padding: 24px 20px;
            flex: 1;
            justify-content: flex-start;
          }

          .bb-main-title {
            font-size: 20px;
          }

          .bb-phone-input-row {
            height: 48px;
          }

          .bb-action-btn {
            height: 48px;
            font-size: 15px;
          }

          .bb-otp-box {
            width: 48px;
            height: 52px;
            font-size: 20px;
          }

          .bb-app-download-section {
            display: none;
          }

          .bb-legal-disclaimer {
            margin-top: auto;
            padding-top: 24px;
          }
        }
      `}</style>

      <div className="bb-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Desktop Close Icon */}
        <button className="bb-modal-close-btn" onClick={closeLoginModal} type="button" aria-label="Close modal">
          <img src={X} alt="close" style={{ width: 14, height: 14 }} />
        </button>

        {/* ── Left Visual / Top Branding Column ── */}
        <div className="bb-left-visual-col">
          {/* Header Row: Logo pinned left, Skip button pinned right on mobile */}
          <div className="bb-modal-mobile-header">
            <img src={ICON_WITH_TEXT_LOGO} alt="Brickbucket" className="bb-brand-logo" />
            <button className="bb-modal-skip-btn" onClick={closeLoginModal} type="button">
              Skip
            </button>
          </div>

          <h2 className="bb-left-heading">Building materials delivered in minutes</h2>
          <p className="bb-left-subtext">
            Order cement, plumbing, electrical, and construction supplies directly to your site.
          </p>

          <div className="bb-perks-list">
            <div className="bb-perk-item">
              <div className="bb-perk-icon-wrap">
                <img src={Lightning} alt="fast" style={{ width: 18, height: 18 }} />
              </div>
              <span>30–90 min site delivery</span>
            </div>

            <div className="bb-perk-item">
              <div className="bb-perk-icon-wrap">
                <img src={CheckCircleGreen} alt="genuine" style={{ width: 18, height: 18 }} />
              </div>
              <span>100% genuine products</span>
            </div>

            <div className="bb-perk-item">
              <div className="bb-perk-icon-wrap">
                <img src={Wallet} alt="secure" style={{ width: 18, height: 18 }} />
              </div>
              <span>Secure online payment</span>
            </div>
          </div>
        </div>

        {/* ── Right Form Column ── */}
        <div className="bb-right-form-col">
          <div className="bb-form-main-section">
            {step === 'MOBILE' ? (
              <>
                <div className="bb-form-header">
                  <h3 className="bb-main-title">Login or Sign Up</h3>
                  {/* <span className="bb-sub-title">
                    Enter your mobile number to checkout or access your site orders
                  </span> */}
                </div>

                {errorMessage && <div className="bb-error-banner">{errorMessage}</div>}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendOtp(false);
                  }}
                  className="bb-form-container"
                >
                  <div className="bb-phone-input-row">
                    <div className="bb-country-code-badge">
                      <span>🇮🇳 +91</span>
                    </div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={10}
                      placeholder="Enter 10 digit mobile number"
                      value={mobileNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setMobileNumber(val);
                        setErrorMessage('');
                      }}
                      className="bb-mobile-input"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || mobileNumber.length !== 10}
                    className="bb-action-btn"
                    style={{
                      opacity: mobileNumber.length === 10 && !loading ? 1 : 0.55,
                      cursor: mobileNumber.length === 10 && !loading ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {loading ? 'Sending OTP...' : 'Continue'}
                  </button>
                </form>

                <span className="bb-legal-disclaimer">
                  By continuing, you agree to our <strong>Terms of Service</strong> & <strong>Privacy Policy</strong>.
                </span>
              </>
            ) : (
              <>
                <div className="bb-form-header">
                  <h3 className="bb-main-title">Verify OTP</h3>
                  <span className="bb-sub-title">
                    We sent a 4-digit code to <strong>+91 {mobileNumber}</strong>
                  </span>
                </div>

                {errorMessage && <div className="bb-error-banner">{errorMessage}</div>}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleVerifyOtp(false);
                  }}
                  className="bb-form-container"
                >
                  <div className="bb-otp-grid">
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (inputRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                        className="bb-otp-box"
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.join('').length !== 4}
                    className="bb-action-btn"
                    style={{
                      opacity: otp.join('').length === 4 && !loading ? 1 : 0.55,
                      cursor: otp.join('').length === 4 && !loading ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </form>

                <div className="bb-otp-footer-actions">
                  <button
                    type="button"
                    className="bb-change-number-btn"
                    onClick={() => {
                      setStep('MOBILE');
                      setErrorMessage('');
                    }}
                  >
                    Change phone number
                  </button>

                  <div className="bb-resend-wrapper">
                    {timer > 0 ? (
                      <span className="bb-timer-text">
                        Resend in <strong style={{ color: '#E9001E' }}>{timer}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="bb-resend-btn"
                        onClick={() => handleSendOtp(false)}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Desktop App Download Badges */}
          <div className="bb-app-download-section">
            <span className="bb-app-download-heading">Order faster & easier everytime with the Brickbucket app</span>
            <div className="bb-badges-row">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bb-black-badge"
              >
                <svg className="bb-play-store-svg" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M3.6 1.8L13.8 12 3.6 22.2c-.4-.4-.6-.9-.6-1.4V3.2c0-.5.2-1 .6-1.4z" />
                  <path fill="#FBBC04" d="M17.4 8.4l-3.6 3.6 3.6 3.6 4.1-2.4c1.2-.7 1.2-1.8 0-2.4l-4.1-2.4z" />
                  <path fill="#EA4335" d="M3.6 1.8l10.2 10.2 3.6-3.6-12.4-7.1c-.5-.3-1-.1-1.4.5z" />
                  <path fill="#34A853" d="M13.8 12l-10.2 10.2c.4.6.9.8 1.4.5l12.4-7.1-3.6-3.6z" />
                </svg>
                <div className="bb-badge-text-wrap">
                  <span className="bb-badge-top-text">GET IT ON</span>
                  <span className="bb-badge-main-text">Google Play</span>
                </div>
              </a>

              <div className="bb-black-badge-muted" title="iOS App coming soon">
                <svg className="bb-apple-svg" viewBox="0 0 24 24" fill="#FFFFFF">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.85-.9.04-2 .6-2.65 1.35-.58.66-1.09 1.73-.96 2.76 1.01.08 2.06-.51 2.69-1.26z" />
                </svg>
                <div className="bb-badge-text-wrap">
                  <span className="bb-badge-top-text">Download on the</span>
                  <span className="bb-badge-main-text">App Store</span>
                </div>
                <span className="bb-coming-soon-tag">COMING SOON</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}